import React, { useMemo } from 'react';
import { Input, Select } from 'antd';
import { STATUS_COLOR } from '../lib/status.js';

const STATUS_PRESETS = [
  {
    key: 'bad',
    label: 'Em problema',
    color: '#ef4444',
    title: 'Consolidado de todos os estados ruins: offline + LOS + dying gasp + power off + config failure',
  },
  {
    key: 'LOS',
    label: 'Só LOS',
    color: STATUS_COLOR[2],
    title:
      'Apenas LOS puro (raw=6 da Huawei MA5800). Quase sempre 0 — a OLT reporta clientes sem sinal como "offline" (raw=2). Para ver clientes caídos, use "Em problema" ou "Offline".',
  },
  {
    key: 'offline',
    label: 'Offline',
    color: STATUS_COLOR[0],
    title: 'Clientes que a OLT não consegue alcançar (raw=2). Mais comum dos estados ruins na MA5800.',
  },
  {
    key: 'online',
    label: 'Online',
    color: STATUS_COLOR[1],
    title: 'Clientes funcionando normalmente',
  },
  {
    key: 'all',
    label: 'Tudo',
    color: '#6b7280',
    title: 'Todos os clientes (online + caídos)',
  },
];

// Aging chips em faixas EXCLUSIVAS [min, max). User clica em "5–30 min" e vê
// apenas ONUs nessa faixa de idade (não cumulativo). Counts batem 1:1 com os
// buckets do backend (`aging_buckets` em `/onus/facets`).
//
// Antes (até 2026-05-15) era cumulativo ≥X — "≥5min" e "≥30min" filtravam
// quase os mesmos clientes (off-by-uma-categoria), o que não ajudava o NOC.
// Decisão: faixas exclusivas + cores graduais por severidade temporal.
export const AGING_PRESETS = [
  { key: 'any',     label: 'Qualquer',  min: 0,    max: 0,    bucketKey: null,       color: '#6b7280' },
  { key: 'lt_5m',   label: '< 5 min',   min: 0,    max: 5,    bucketKey: 'lt_5m',    color: '#84cc16' },
  { key: '5m_30m',  label: '5–30 min',  min: 5,    max: 30,   bucketKey: '5m_30m',   color: '#fbbf24' },
  { key: '30m_1h',  label: '30 min–1 h',min: 30,   max: 60,   bucketKey: '30m_1h',   color: '#f59e0b' },
  { key: '1h_6h',   label: '1–6 h',     min: 60,   max: 360,  bucketKey: '1h_6h',    color: '#f97316' },
  { key: '6h_24h',  label: '6–24 h',    min: 360,  max: 1440, bucketKey: '6h_24h',   color: '#dc2626' },
  { key: '24h_plus',label: '> 24 h',    min: 1440, max: 0,    bucketKey: '24h_plus', color: '#7c2d12' },
];

const chipStyle = (active, color) => ({
  padding: '6px 12px',
  borderRadius: 8,
  border: `1.5px solid ${active ? color : '#e5e7eb'}`,
  background: active ? `${color}1a` : 'white',
  color: active ? color : '#374151',
  fontSize: 13,
  fontWeight: active ? 600 : 500,
  cursor: 'pointer',
  transition: 'all 120ms',
  whiteSpace: 'nowrap',
});

const groupLabelStyle = {
  fontSize: 11,
  color: '#6b7280',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginRight: 4,
};

/**
 * FiltersBar v2 — barra rica de filtros para o Mapa NOC 2026.
 *
 * Layout em 3 linhas:
 *   1) status chips com counts facetados + counts shown/matched/total + fonte
 *   2) aging chips cumulativos (≥X min) com counts derivados de aging_buckets
 *   3) search input + OLT multi-select + botão "Limpar filtros"
 */
/**
 * `agingPresetKey` (string) é a chave do preset selecionado ("any" por default).
 * MapPage extrai `min`/`max` desse preset e envia ao backend.
 */
export function FiltersBar({
  statusFilter,
  onStatusChange,
  search,
  onSearchChange,
  selectedOlts,
  onOltsChange,
  agingPresetKey = 'any',
  onAgingPresetChange,
  byOlt = [],
  byStatus = {},
  agingBuckets = {},
  shownCount,
  matchedCount,
  totalCount,
  source,
  isFetching,
  generatedAt, // ISO string do snapshot — mostra "atualizado há Xs"
}) {
  const ageOfSnapshot = useMemo(() => {
    if (!generatedAt) return null;
    const ms = Date.now() - new Date(generatedAt).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    const s = Math.floor(ms / 1000);
    if (s < 5) return 'agora';
    if (s < 60) return `${s}s atrás`;
    const m = Math.floor(s / 60);
    return `${m}min atrás`;
  }, [generatedAt]);
  const oltOptions = useMemo(
    () =>
      byOlt.map((o) => ({
        value: o.olt_id,
        label: `${o.olt_code || `OLT ${o.olt_id}`} · ${o.bad} em problema · ${o.total} total`,
      })),
    [byOlt]
  );

  // Total bad = soma dos 6 buckets (usado pelo "Qualquer").
  const agingTotal = useMemo(() => {
    const order = ['lt_5m', '5m_30m', '30m_1h', '1h_6h', '6h_24h', '24h_plus'];
    return order.reduce((acc, k) => acc + (agingBuckets[k] || 0), 0);
  }, [agingBuckets]);

  const hasActiveFilters =
    Boolean(search) ||
    (selectedOlts && selectedOlts.length > 0) ||
    (agingPresetKey && agingPresetKey !== 'any');

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '14px 16px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f9fafb',
      }}
    >
      {/* Row 1: status chips + counts + source */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={groupLabelStyle}>Status:</span>
        {STATUS_PRESETS.map((p) => {
          const facetN = byStatus?.[p.key];
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onStatusChange(p.key)}
              style={chipStyle(statusFilter === p.key, p.color)}
              title={p.title}
            >
              {p.label}
              {facetN != null && (
                <span style={{ marginLeft: 6, opacity: 0.75 }}>
                  · {facetN.toLocaleString('pt-BR')}
                </span>
              )}
            </button>
          );
        })}

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            fontSize: 12,
            color: '#6b7280',
          }}
        >
          {matchedCount != null && (
            <span>
              <strong style={{ color: '#111827' }}>
                {shownCount}/{matchedCount.toLocaleString('pt-BR')}
              </strong>
              {totalCount != null && (
                <span style={{ color: '#9ca3af' }}>
                  {' '}
                  de {totalCount.toLocaleString('pt-BR')}
                </span>
              )}
            </span>
          )}
          {isFetching && (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>atualizando…</span>
          )}
          {!isFetching && ageOfSnapshot && (
            <span
              style={{ fontSize: 11, color: '#9ca3af' }}
              title={`Snapshot gerado em ${generatedAt}`}
            >
              {ageOfSnapshot}
            </span>
          )}
          {source && (
            <span
              style={{
                fontSize: 10,
                padding: '2px 8px',
                background:
                  source === 'postgres'
                    ? '#dbeafe'
                    : source.startsWith('redis')
                    ? '#fef3c7'
                    : '#fee2e2',
                color:
                  source === 'postgres'
                    ? '#1e40af'
                    : source.startsWith('redis')
                    ? '#92400e'
                    : '#991b1b',
                borderRadius: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
              title={`Fonte de dados: ${source}`}
            >
              {source}
            </span>
          )}
        </div>
      </div>

      {/* Row 2: aging chips em faixas EXCLUSIVAS — visíveis SÓ quando o
          status atual filtra estados ruins. Counts vêm de aging_buckets do
          backend, que conta WHERE status IN STATUS_BAD_EXT — não faz sentido
          quando user pede "Online" ou "Tudo". */}
      {['bad', 'LOS', 'offline', 'dying_gasp', 'power_off', 'config_failure'].includes(
        statusFilter
      ) && (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={groupLabelStyle}>Idade:</span>
        {AGING_PRESETS.map((p) => {
          const active = (agingPresetKey || 'any') === p.key;
          const n = p.bucketKey ? agingBuckets[p.bucketKey] : agingTotal;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => onAgingPresetChange?.(p.key)}
              style={chipStyle(active, p.color)}
              title={
                p.key === 'any'
                  ? 'Todas as faixas de idade'
                  : `ONUs com idade entre ${p.min}min e ${p.max ? p.max + 'min' : '∞'}`
              }
            >
              {p.label}
              {n != null && (
                <span style={{ marginLeft: 6, opacity: 0.75 }}>
                  · {n.toLocaleString('pt-BR')}
                </span>
              )}
            </button>
          );
        })}
      </div>
      )}

      {/* Row 3: search + OLT multi-select + clear */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input.Search
          allowClear
          placeholder="Cliente, contrato, SN, CEP, OLT…"
          value={search || ''}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ width: 320, flex: '0 0 auto' }}
          size="middle"
        />

        <Select
          mode="multiple"
          maxTagCount="responsive"
          allowClear
          placeholder="Filtrar por OLT"
          value={selectedOlts}
          onChange={onOltsChange}
          options={oltOptions}
          style={{ minWidth: 280, flex: '0 0 auto' }}
          size="middle"
          showSearch
          filterOption={(input, option) =>
            String(option?.label || '').toLowerCase().includes(input.toLowerCase())
          }
        />

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              onSearchChange('');
              onOltsChange([]);
              onAgingPresetChange?.('any');
            }}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1.5px solid #e5e7eb',
              background: 'white',
              color: '#374151',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Limpar filtros
          </button>
        )}
      </div>
    </div>
  );
}
